import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Rate,
  Tag, Space, Tooltip, Card, Row, Col, Statistic,
  Badge, Typography, Popconfirm, Divider, Alert,
} from 'antd';
import { staticApi } from '../utils/staticApi';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, StarOutlined,
  PhoneOutlined, MailOutlined, GlobalOutlined, SafetyCertificateOutlined,
  TeamOutlined, ToolOutlined, CheckCircleOutlined, StopOutlined,
  WarningOutlined, BankOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCondoStore } from '../store/condoStore';
import type { Vendor, VendorCategory, VendorStatus } from '../types';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CATEGORY_LABELS: Record<VendorCategory, string> = {
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  hvac: 'HVAC',
  landscaping: 'Landscaping',
  cleaning: 'Cleaning',
  security: 'Security',
  elevator: 'Elevator',
  general: 'General',
  other: 'Other',
};

const CATEGORY_COLORS: Record<VendorCategory, string> = {
  plumbing: 'blue',
  electrical: 'gold',
  hvac: 'cyan',
  landscaping: 'green',
  cleaning: 'purple',
  security: 'red',
  elevator: 'orange',
  general: 'default',
  other: 'default',
};

const STATUS_CONFIG: Record<VendorStatus, { color: string; icon: React.ReactNode; label: string }> = {
  ACTIVE: { color: 'success', icon: <CheckCircleOutlined />, label: 'Active' },
  INACTIVE: { color: 'default', icon: <StopOutlined />, label: 'Inactive' },
  BLACKLISTED: { color: 'error', icon: <WarningOutlined />, label: 'Blacklisted' },
};

const Vendors: React.FC = () => {
  const {
    vendors, fetchVendors, createVendor, updateVendor, deleteVendor, rateVendor,
    currentCondo,
    isLoading,
  } = useCondoStore() as any;

  const [modalOpen, setModalOpen] = useState(false);
  const [rateModalOpen, setRateModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [ratingVendor, setRatingVendor] = useState<Vendor | null>(null);
  const [newRating, setNewRating] = useState<number>(3);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [form] = Form.useForm();

  useEffect(() => {
    if (currentCondo?.id) fetchVendors(currentCondo.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetchVendors is a stable zustand action
  }, [currentCondo?.id]);

  const filtered = vendors.filter((v: Vendor) => {
    if (categoryFilter !== 'all' && v.category !== categoryFilter) return false;
    if (statusFilter !== 'all' && v.status !== statusFilter) return false;
    return true;
  });

  const activeCount = vendors.filter((v: Vendor) => v.status === 'ACTIVE').length;
  const avgRating = vendors.length
    ? (vendors.reduce((s: number, v: Vendor) => s + (v.rating ?? 0), 0) / vendors.filter((v: Vendor) => v.rating != null).length || 0)
    : 0;
  const categoryCounts = vendors.reduce((acc: Record<string, number>, v: Vendor) => {
    acc[v.category] = (acc[v.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const topCategory = (Object.entries(categoryCounts) as [string, number][]).sort((a, b) => b[1] - a[1])[0]?.[0];

  const handleOpenCreate = () => {
    setEditingVendor(null);
    form.resetFields();
    form.setFieldsValue({ status: 'ACTIVE', category: 'general' });
    setModalOpen(true);
  };

  const handleOpenEdit = (v: Vendor) => {
    setEditingVendor(v);
    form.setFieldsValue({
      ...v,
      insuranceExpiry: v.insuranceExpiry ? dayjs(v.insuranceExpiry) : undefined,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        condoId: currentCondo?.id || '',
        insuranceExpiry: values.insuranceExpiry
          ? dayjs(values.insuranceExpiry).format('YYYY-MM-DD')
          : undefined,
      };
      if (editingVendor) {
        await updateVendor(editingVendor.id, payload);
        staticApi.message.success('Vendor updated');
      } else {
        await createVendor(payload);
        staticApi.message.success('Vendor added');
      }
      setModalOpen(false);
    } catch {
      // validation error
    }
  };

  const handleDelete = async (id: string) => {
    await deleteVendor(id);
    staticApi.message.success('Vendor removed');
  };

  const handleOpenRate = (v: Vendor) => {
    setRatingVendor(v);
    setNewRating(v.rating ?? 3);
    setRateModalOpen(true);
  };

  const handleRateSubmit = async () => {
    if (!ratingVendor) return;
    await rateVendor(ratingVendor.id, newRating);
    staticApi.message.success('Rating saved');
    setRateModalOpen(false);
  };

  const columns: ColumnsType<Vendor> = [
    {
      title: 'Vendor',
      key: 'vendor',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.name}</Text>
          {r.contactPerson && <Text type="secondary" style={{ fontSize: 12 }}>{r.contactPerson}</Text>}
        </Space>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (cat: VendorCategory) => (
        <Tag color={CATEGORY_COLORS[cat]}>{CATEGORY_LABELS[cat] || cat}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: VendorStatus) => {
        const cfg = STATUS_CONFIG[s] || STATUS_CONFIG.ACTIVE;
        return <Badge status={cfg.color as any} text={cfg.label} />;
      },
    },
    {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      render: (r: number | undefined) =>
        r != null ? <Rate disabled defaultValue={r} style={{ fontSize: 14 }} /> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          {r.phone && (
            <Space size={4}>
              <PhoneOutlined style={{ fontSize: 11, color: '#888' }} />
              <Text style={{ fontSize: 12 }}>{r.phone}</Text>
            </Space>
          )}
          {r.email && (
            <Space size={4}>
              <MailOutlined style={{ fontSize: 11, color: '#888' }} />
              <Text style={{ fontSize: 12 }}>{r.email}</Text>
            </Space>
          )}
          {r.website && (
            <Space size={4}>
              <GlobalOutlined style={{ fontSize: 11, color: '#888' }} />
              <a href={r.website} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                Website
              </a>
            </Space>
          )}
        </Space>
      ),
    },
    {
      title: 'Insurance',
      key: 'insurance',
      render: (_, r) => {
        if (!r.insuranceExpiry) return <Text type="secondary">—</Text>;
        const expired = dayjs(r.insuranceExpiry).isBefore(dayjs());
        return (
          <Space size={4}>
            <SafetyCertificateOutlined style={{ color: expired ? '#ff4d4f' : '#52c41a' }} />
            <Text type={expired ? 'danger' : undefined} style={{ fontSize: 12 }}>
              {dayjs(r.insuranceExpiry).format('MMM D, YYYY')}
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) => (
        <Space>
          <Tooltip title="Rate vendor">
            <Button size="small" icon={<StarOutlined />} onClick={() => handleOpenRate(r)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(r)} />
          </Tooltip>
          <Popconfirm
            title="Remove this vendor?"
            onConfirm={() => handleDelete(r.id)}
            okText="Remove"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Remove">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <ToolOutlined style={{ marginRight: 10, color: '#1890ff' }} />
            Vendor & Contractor Management
          </Title>
          <Text type="secondary">Manage approved vendors, track ratings and insurance</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
          Add Vendor
        </Button>
      </div>

      {/* KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8} md={6}>
          <Card>
            <Statistic
              title="Total Vendors"
              value={vendors.length}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card>
            <Statistic
              title="Active Vendors"
              value={activeCount}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card>
            <Statistic
              title="Avg. Rating"
              value={avgRating.toFixed(1)}
              suffix="/ 5"
              prefix={<StarOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card>
            <Statistic
              title="Top Category"
              value={topCategory ? CATEGORY_LABELS[topCategory as VendorCategory] : '—'}
              prefix={<BankOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Expired Insurance Warning */}
      {vendors.some((v: Vendor) => v.insuranceExpiry && dayjs(v.insuranceExpiry).isBefore(dayjs())) && (
        <Alert
          type="warning"
          showIcon
          message="Insurance Expiry Alert"
          description="One or more vendors have expired insurance coverage. Please review and update their certificates."
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Filters */}
      <Space style={{ marginBottom: 16 }} wrap>
        <Select value={categoryFilter} onChange={setCategoryFilter} style={{ width: 160 }}>
          <Option value="all">All Categories</Option>
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
            <Option key={k} value={k}>{v}</Option>
          ))}
        </Select>
        <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 140 }}>
          <Option value="all">All Statuses</Option>
          <Option value="ACTIVE">Active</Option>
          <Option value="INACTIVE">Inactive</Option>
          <Option value="BLACKLISTED">Blacklisted</Option>
        </Select>
      </Space>

      {/* Table */}
      <Card>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 10 }}
          expandable={{
            expandedRowRender: (r) => (
              <div style={{ padding: '8px 0' }}>
                <Row gutter={24}>
                  {r.licenseNumber && (
                    <Col span={8}>
                      <Text type="secondary">License: </Text>
                      <Text>{r.licenseNumber}</Text>
                    </Col>
                  )}
                  {r.insuranceProvider && (
                    <Col span={8}>
                      <Text type="secondary">Insurance Provider: </Text>
                      <Text>{r.insuranceProvider}</Text>
                    </Col>
                  )}
                  {r.notes && (
                    <Col span={24} style={{ marginTop: 8 }}>
                      <Text type="secondary">Notes: </Text>
                      <Text>{r.notes}</Text>
                    </Col>
                  )}
                </Row>
              </div>
            ),
            rowExpandable: (r) => !!(r.licenseNumber || r.insuranceProvider || r.notes),
          }}
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        title={editingVendor ? 'Edit Vendor' : 'Add Vendor'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText={editingVendor ? 'Save Changes' : 'Add Vendor'}
        width={640}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="name" label="Company Name" rules={[{ required: true }]}>
                <Input placeholder="ABC Plumbing Ltd." />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                <Select>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                    <Option key={k} value={k}>{v}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="contactPerson" label="Contact Person">
                <Input placeholder="John Smith" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="ACTIVE">Active</Option>
                  <Option value="INACTIVE">Inactive</Option>
                  <Option value="BLACKLISTED">Blacklisted</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="phone" label="Phone">
                <Input placeholder="(416) 555-0100" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="email" label="Email">
                <Input placeholder="contact@vendor.com" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="website" label="Website">
                <Input placeholder="https://vendor.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="licenseNumber" label="License Number">
                <Input placeholder="LIC-12345" />
              </Form.Item>
            </Col>
          </Row>
          <Divider plain>Insurance</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="insuranceProvider" label="Insurance Provider">
                <Input placeholder="Intact Financial" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="insuranceExpiry" label="Insurance Expiry">
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} placeholder="Any additional notes..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Rate Vendor Modal */}
      <Modal
        title={`Rate: ${ratingVendor?.name}`}
        open={rateModalOpen}
        onOk={handleRateSubmit}
        onCancel={() => setRateModalOpen(false)}
        okText="Save Rating"
        width={360}
      >
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            How would you rate this vendor's work quality and reliability?
          </Text>
          <Rate
            value={newRating}
            onChange={setNewRating}
            style={{ fontSize: 32 }}
          />
          <Text style={{ display: 'block', marginTop: 8, color: '#faad14' }}>
            {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][newRating]}
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default Vendors;
